// ============================================================================
// Save the open project, then start a new one (review of the outage lane,
// 2026-09-30)
// ----------------------------------------------------------------------------
// "+ New Project", "Open the recovered quote" and "Rebuild this quote" save
// the open project first, then clear the working context (runReset inside
// handleStartNewProject in App.tsx). This is the decision that follows that
// save.
//
// While QuoteMaker's server is not answering, the save keeps the edits on
// this device and rejects without a message, because the server banner owns
// the status (use-project-persistence). runReset awaited it with no catch, so
// the click closed the dialog and did nothing, and once the banner was
// dismissed nothing on screen said why. Now the operator is told what
// happened and chooses (Peter's Q1: never silently blocked):
//   - the changes are kept under the project's id: they come back when that
//     project opens, so going on loses nothing;
//   - a project never saved, or changes that could not be kept: going on
//     discards them, said in one sentence, and Stay here is the default.
// Any other failure is left as it was: a save the server refused shows the
// save-error banner and a toast, and a newer version to review opens its own
// notice. Only the outage case is new here.
// ============================================================================

import { isServerUnavailableError } from '../services/network-resilience';
import { isQuoteRunActiveError } from '../services/quote-run-open-guard';

export interface StartNewConfirmRequest {
    title: string;
    message: string;
    confirmText: string;
    cancelText: string;
    confirmColor: 'blue' | 'red';
    icon: 'info' | 'danger';
    defaultFocus: 'cancel';
    hint: string;
    onConfirm: () => void;
    onCancel: () => void;
}

export interface SaveThenStartNewInput {
    /** Saves the open project. Rejects when the server did not take it. */
    save: () => Promise<unknown>;
    /** Clears the working context and runs the caller's follow-on. */
    start: () => void;
    /** Forgets what this screen kept on the device (its id's copy, a new project's copy). */
    discardOnScreen: () => void;
    showConfirm: (request: StartNewConfirmRequest) => void;
    /** The open project's id, read after the save settled. Null: never saved. */
    projectId: () => string | null | undefined;
    projectTitle: () => string;
    /** The action's button label: 'Start New', 'Open it', 'Rebuild'. */
    actionText: string;
}

const STAY_HINT = 'Staying keeps this screen. It saves by itself when the server answers.';

function discardLabel(actionText: string) {
    return `Discard and ${actionText.trim().toLowerCase()}`;
}

/** Resolves true when the new project was started. Never rejects on a failed save. */
export async function saveThenStartNew(input: SaveThenStartNewInput): Promise<boolean> {
    try {
        await input.save();
    } catch (err) {
        // A Quote Run refused and showed its message. Any other failure is
        // not this outage path and keeps its old behaviour (a server refusal
        // shows the save-error banner and a toast), minus the unhandled
        // rejection.
        if (isQuoteRunActiveError(err)) return false;
        if (!isServerUnavailableError(err)) {
            console.warn('[QM] The current project did not save, so nothing was started:', (err as { message?: string })?.message || err);
            return false;
        }
        const projectId = input.projectId() || null;
        const title = input.projectTitle();
        const kept = (err as { keptOnDevice?: unknown })?.keptOnDevice === true;
        if (projectId && kept) {
            input.showConfirm({
                title: 'QuoteMaker is not answering',
                message: `"${title}" could not be saved yet because QuoteMaker can't reach its server. Your changes are kept on this device and come back when you open "${title}" again.`,
                confirmText: input.actionText,
                cancelText: 'Stay here',
                confirmColor: 'blue',
                icon: 'info',
                defaultFocus: 'cancel',
                hint: STAY_HINT,
                onConfirm: () => { input.start(); },
                onCancel: () => {},
            });
            return false;
        }
        input.showConfirm({
            title: 'QuoteMaker is not answering',
            message: projectId
                ? `"${title}" could not be saved because QuoteMaker can't reach its server, and its changes could not be kept on this device. Going on now discards them.`
                : 'This new project could not be saved yet because QuoteMaker can\'t reach its server. Going on now discards it.',
            confirmText: discardLabel(input.actionText),
            cancelText: 'Stay here',
            confirmColor: 'red',
            // Discarding is the primary here and cannot be undone: the
            // dialog's danger tone says so in text.
            icon: 'danger',
            defaultFocus: 'cancel',
            hint: STAY_HINT,
            onConfirm: () => {
                input.discardOnScreen();
                input.start();
            },
            onCancel: () => {},
        });
        return false;
    }
    input.start();
    return true;
}
